import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface BlogAudioPlayerProps {
  audioUrl: string;
  title: string;
}

const BlogAudioPlayer: React.FC<BlogAudioPlayerProps> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [miniPlayerMounted, setMiniPlayerMounted] = useState(false);
  const [miniPlayerVisible, setMiniPlayerVisible] = useState(false);

  // Mount mini player on first show
  useEffect(() => {
    if (showMiniPlayer && !miniPlayerMounted) {
      setMiniPlayerMounted(true);
      // Trigger animation after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMiniPlayerVisible(true);
        });
      });
    } else if (showMiniPlayer && miniPlayerMounted) {
      // Already mounted, just show it
      setMiniPlayerVisible(true);
    } else if (!showMiniPlayer && miniPlayerMounted) {
      // Hide it
      setMiniPlayerVisible(false);
    }
  }, [showMiniPlayer, miniPlayerMounted]);

  // Format time helper
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.volume = volume;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [volume]);

  // Scroll detection for mini player
  useEffect(() => {
    const handleScroll = () => {
      if (playerRef.current) {
        const rect = playerRef.current.getBoundingClientRect();
        // Show mini player when full player is scrolled past (not visible)
        setShowMiniPlayer(rect.bottom < 0);
      }
    };

    // Check on mount and scroll
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Play/Pause toggle
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Seek in progress bar
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Volume control
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 0.7;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // Change playback speed
  const cyclePlaybackRate = () => {
    const rates = [0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  // Mini Player Component - Memoized
  const MiniPlayerComponent = React.memo(() => (
    <div
      style={{ 
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: miniPlayerVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(150px)',
        zIndex: 9999,
        width: '100%',
        maxWidth: '896px',
        padding: '0 1rem',
        transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
        opacity: miniPlayerVisible ? 1 : 0,
        pointerEvents: miniPlayerVisible ? 'auto' : 'none'
      }}
      >
        <div 
          className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-xl shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.5), rgba(30, 41, 59, 0.5))',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 80px rgba(6, 182, 212, 0.1)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
        >
        <div className="px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Play/Pause */}
          <button
            onClick={togglePlayPause}
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 flex items-center justify-center transition-all flex-shrink-0"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 text-white" />
            ) : (
              <Play className="w-4 h-4 text-white ml-0.5" />
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1 min-w-0">
            <div
              className="relative h-1.5 bg-slate-700/50 rounded-full cursor-pointer"
              onClick={handleProgressClick}
            >
              <div
                className="absolute h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Time */}
          <div className="text-xs text-slate-400 font-mono whitespace-nowrap">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          {/* Close */}
          <button
            onClick={() => setShowMiniPlayer(false)}
            aria-label="Close mini player"
            className="text-slate-400 hover:text-white transition-colors flex-shrink-0 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        </div>
        </div>
      </div>
  ));

  return (
    <>
      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Full Player (Inline) - Compact */}
      <div
        ref={playerRef}
        className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 mb-8 transition-all duration-300"
      >
        <div className="flex items-center gap-4">
          {/* Left: Play/Pause */}
          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            className="w-12 h-12 flex-shrink-0 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 disabled:from-slate-600 disabled:to-slate-700 flex items-center justify-center transition-all hover:scale-105 shadow-lg"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </button>

          {/* Center: Progress Bar and Time */}
          <div className="flex-1 min-w-0" style={{ marginTop: '20px' }}>
            <div
              className="relative h-2 bg-slate-700/50 rounded-full cursor-pointer group mb-1"
              onClick={handleProgressClick}
            >
              <div
                className="absolute h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute w-3 h-3 bg-white rounded-full shadow-lg top-1/2 -translate-y-1/2 transition-all opacity-0 group-hover:opacity-100"
                style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Right: Volume & Speed */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Volume */}
            <div className="hidden sm:flex items-center gap-2">
              <button 
                onClick={toggleMute} 
                aria-label={isMuted || volume === 0 ? "Unmute audio" : "Mute audio"}
                className="text-slate-400 hover:text-cyan-400 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-16 accent-cyan-500"
              />
            </div>

            {/* Speed */}
            <button
              onClick={cyclePlaybackRate}
              aria-label="Change playback speed"
              className="px-2.5 py-1 min-h-[44px] text-xs font-mono bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-cyan-400 rounded-lg transition-colors flex items-center justify-center"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>

      {/* Mini Player (Sticky) - Rendered for smooth transitions */}
      {miniPlayerMounted && typeof document !== 'undefined' && createPortal(
        <MiniPlayerComponent key="mini-player" />,
        document.body
      )}

    </>
  );
};

export default React.memo(BlogAudioPlayer);
